'use client'

import { useTranslations } from 'next-intl'

import { useAnalyticsAudience } from '@/features/clubs/hooks/useClubAnalytics'
import {
  type AnalyticsShare,
  maxShareKey
} from '@/features/clubs/lib/clubAnalytics'

import S from '../ClubProfileAnalytics.module.scss'
import BlockState from './BlockState'
import type { MiniBarRowItem } from './MiniBarRow'
import MiniChartCard from './MiniChartCard'

const toItems = (
  shares: AnalyticsShare[],
  label: (key: string) => string
): MiniBarRowItem[] => {
  const max = maxShareKey(shares)
  return shares.map(share => ({
    label: label(share.key),
    percent: share.percent,
    count: share.count,
    active: share.key === max
  }))
}

const ACTIVITY_LABEL: Record<string, string> = {
  none: 'analytics.activity.lt1',
  '1-3': 'analytics.activity.between1and3',
  '4-6': 'analytics.activity.between4and6',
  '7+': 'analytics.activity.gte7'
}

/**
 * Gender, Age and Activity (US-ATH-08.2): the share of the fans whose value
 * is known, the largest category highlighted; a block nobody has a value
 * for shows its empty state.
 */
const AudienceBlocks = () => {
  const t = useTranslations('club.stats')
  const { data, isError, refetch } = useAnalyticsAudience()

  if (!data && !isError) {
    return <div className={S.leftSkeleton} aria-busy="true" />
  }

  const stateOf = (
    shares: AnalyticsShare[] | undefined,
    title: string,
    description: string
  ) =>
    isError ? (
      <BlockState error onRetry={() => refetch()} />
    ) : shares?.some(share => share.count > 0) ? undefined : (
      <BlockState title={title} description={description} />
    )

  return (
    <>
      <MiniChartCard
        title={t('analytics.gender.title')}
        items={toItems(data?.gender ?? [], key =>
          t(`analytics.gender.${key as 'female' | 'male' | 'other'}`)
        )}
        state={stateOf(
          data?.gender,
          t('analytics.empty.gender'),
          t('analytics.empty.fansJoin')
        )}
      />
      <MiniChartCard
        title={t('analytics.age')}
        items={toItems(data?.age ?? [], key => key)}
        state={stateOf(
          data?.age,
          t('analytics.empty.age'),
          t('analytics.empty.fansJoin')
        )}
      />
      <MiniChartCard
        title={t('analytics.activity.title')}
        items={toItems(data?.activity ?? [], key =>
          ACTIVITY_LABEL[key] ? t(ACTIVITY_LABEL[key]) : key
        )}
        chartsClassName={S.activityCharts}
        state={stateOf(
          data?.activity,
          t('analytics.empty.activity'),
          t('analytics.empty.activityText')
        )}
      />
    </>
  )
}

export default AudienceBlocks
